/**
 * A person's voicemail greetings, as Stitch keeps them in the `voicemailGreeting` setting (1.12.33,
 * Teampace 2976): which greeting plays normally, and the personal greetings there are, each words
 * Stitch typed (the phone system speaks them) or a recording someone made. Neutral: the adapter
 * turns the phone system's own greetings into this and back. Browser-safe (no Node APIs).
 *
 * The setting holds the whole state as JSON, so a change set's undo puts back exactly what was
 * there (which greeting played, and every personal greeting), and its read-back compares the lot.
 *
 * Decisions (Joey, Oct 30 2026):
 * - The typed greeting goes in personal greeting 1 when that's empty or already holds Stitch's
 *   typed words. When personal 1 holds a recording, it goes in the personal greeting that already
 *   holds Stitch's typed words, else the first empty one. A recording is never overwritten.
 * - Only the normal greeting changes; the do not disturb, on a call and after hours greetings stay.
 */

/** One personal greeting: words to speak (in `lang`), or a recording (the phone system's own file, never changed here). */
export type PersonalGreeting = { say: string; lang: string } | { file: string };

export interface Greetings {
  /**
   * Which greeting plays normally: "personal:N", "standard", or "other:<the phone system's own
   * word>" for a kind Stitch doesn't name (kept as it is).
   */
  plays: string;
  /** The personal greetings there are, by number ("1" to "9"). Empty ones aren't listed. */
  personal: Record<string, PersonalGreeting>;
}

/**
 * How many personal greetings a mailbox has. The phone system lists them as they're made
 * (personal1, personal2, …); nine is what Stitch allows for. TO CONFIRM on the phone system: a
 * slot it doesn't keep shows as a failed change (the read-back), never a silent one.
 */
export const GREETING_SLOTS = 9;

/**
 * Longest typed greeting, in characters. A voicemail greeting is a few sentences: 300 characters
 * is about 50 words, 20 seconds or so spoken, which is about as long as callers wait before the
 * beep (longer ones get hung up on). It also keeps the setting small: the words are stored
 * Base64-encoded, a third longer again.
 */
export const TYPED_GREETING_MAX = 300;

const SLOT = /^[1-9]$/;
const LANG = /^[A-Za-z]{2,3}(?:-[A-Za-z0-9]{2,8})?$/;
// eslint-disable-next-line no-control-regex
const CONTROL = /[\u0000-\u0008\u000b-\u001f\u007f]/;

/** Words to speak, tidied as they're saved: one line, single spaces. */
export function tidyGreetingText(text: string): string {
  return text.replace(/\s+/g, ' ').trim();
}

export function isTyped(p: PersonalGreeting | undefined): p is { say: string; lang: string } {
  return Boolean(p && 'say' in p);
}

export function isRecorded(p: PersonalGreeting | undefined): p is { file: string } {
  return Boolean(p && 'file' in p);
}

/** The setting's value: canonical JSON (keys in order, slots by number), so equal states compare equal. */
export function formatGreetings(g: Greetings): string {
  const personal: Record<string, PersonalGreeting> = {};
  for (const slot of Object.keys(g.personal).sort((a, b) => Number(a) - Number(b))) {
    const p = g.personal[slot]!;
    personal[slot] = isRecorded(p) ? { file: p.file } : { lang: p.lang, say: p.say };
  }
  return JSON.stringify({ personal, plays: g.plays });
}

/** The setting's value read back, or null when it isn't one. */
export function parseGreetings(value: string): Greetings | null {
  let raw: unknown;
  try {
    raw = JSON.parse(value);
  } catch {
    return null;
  }
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const o = raw as Record<string, unknown>;
  if (typeof o.plays !== 'string' || !o.personal || typeof o.personal !== 'object' || Array.isArray(o.personal)) return null;
  const personal: Record<string, PersonalGreeting> = {};
  for (const [slot, p] of Object.entries(o.personal as Record<string, unknown>)) {
    if (!SLOT.test(slot) || !p || typeof p !== 'object') return null;
    const x = p as Record<string, unknown>;
    if (typeof x.file === 'string' && x.say === undefined) personal[slot] = { file: x.file };
    else if (typeof x.say === 'string' && typeof x.lang === 'string' && x.file === undefined) personal[slot] = { say: x.say, lang: x.lang };
    else return null;
  }
  return { plays: o.plays, personal };
}

/** Plain-language problem with a value of the setting, or null. */
export function greetingProblem(value: string): string | null {
  const g = parseGreetings(value);
  if (!g) return 'Not a voicemail greeting Stitch understands';
  if (!/^(standard|personal:[1-9]|other:[^\u0000-\u001f]{0,40})$/.test(g.plays)) return 'Choose which greeting plays';
  const slot = playingSlot(g);
  if (slot && !g.personal[slot]) return `Personal greeting ${slot} is empty, so it can’t be the one that plays`;
  for (const [n, p] of Object.entries(g.personal)) {
    if (isRecorded(p)) {
      if (!p.file || p.file.length > 200 || CONTROL.test(p.file)) return `Personal greeting ${n} isn’t a recording Stitch understands`;
      continue;
    }
    if (!p.say.trim()) return 'Type the words callers should hear';
    if (p.say.length > TYPED_GREETING_MAX) return `At most ${TYPED_GREETING_MAX} characters`;
    if (CONTROL.test(p.say)) return 'Use plain words, without special characters';
    if (!LANG.test(p.lang)) return 'Not a language Stitch knows';
  }
  return null;
}

/** The personal greeting that plays, or null for the standard one (or another kind). */
export function playingSlot(g: Greetings): string | null {
  const m = /^personal:([1-9])$/.exec(g.plays);
  return m ? m[1]! : null;
}

export type GreetingPlaying =
  | { kind: 'standard' }
  | { kind: 'typed'; slot: string; text: string }
  | { kind: 'recorded'; slot: string }
  /** A personal greeting that's empty, or a kind Stitch doesn't name (set on the phone system). */
  | { kind: 'other' };

export function playingNow(g: Greetings): GreetingPlaying {
  if (g.plays === 'standard') return { kind: 'standard' };
  const slot = playingSlot(g);
  const p = slot ? g.personal[slot] : undefined;
  if (slot && isTyped(p)) return { kind: 'typed', slot, text: p.say };
  if (slot && isRecorded(p)) return { kind: 'recorded', slot };
  return { kind: 'other' };
}

/** Stitch's typed greeting, if there is one: the one that plays, else the lowest-numbered. */
export function typedGreeting(g: Greetings): { slot: string; text: string; lang: string } | null {
  const slot = playingSlot(g);
  const playing = slot ? g.personal[slot] : undefined;
  if (slot && isTyped(playing)) return { slot, text: playing.say, lang: playing.lang };
  for (const n of slotsInOrder(g)) {
    const p = g.personal[n];
    if (isTyped(p)) return { slot: n, text: p.say, lang: p.lang };
  }
  return null;
}

/** The recorded greetings, by number. */
export function recordedSlots(g: Greetings): string[] {
  return slotsInOrder(g).filter((n) => isRecorded(g.personal[n]));
}

/**
 * Where a typed greeting goes: personal greeting 1 when it's empty or holds Stitch's typed words;
 * when it holds a recording, the greeting that already holds Stitch's typed words, else the first
 * empty one. Null when every personal greeting holds a recording.
 */
export function slotForTyped(g: Greetings, slots = GREETING_SLOTS): string | null {
  const first = g.personal['1'];
  if (!first || isTyped(first)) return '1';
  for (let n = 2; n <= slots; n++) if (isTyped(g.personal[String(n)])) return String(n);
  for (let n = 2; n <= slots; n++) if (!g.personal[String(n)]) return String(n);
  return null;
}

export class GreetingChoiceError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'GreetingChoiceError';
  }
}

/** The greetings with these words saved (where `slotForTyped` says) and playing. Never touches a recording. */
export function withTypedGreeting(g: Greetings, text: string, lang: string, slots = GREETING_SLOTS): Greetings {
  const say = tidyGreetingText(text);
  if (!say) throw new GreetingChoiceError('Type the words callers should hear.');
  if (say.length > TYPED_GREETING_MAX) throw new GreetingChoiceError(`Keep it to ${TYPED_GREETING_MAX} characters: a few sentences.`);
  if (CONTROL.test(say)) throw new GreetingChoiceError('Use plain words, without special characters.');
  const slot = slotForTyped(g, slots);
  if (!slot) {
    throw new GreetingChoiceError(
      `Every personal greeting (all ${slots}) holds a recording, and Stitch never records over one. Delete one from voicemail on the phone, then try again.`
    );
  }
  return { plays: `personal:${slot}`, personal: { ...g.personal, [slot]: { say, lang: LANG.test(lang) ? lang : 'en' } } };
}

/** The greetings with another one playing: the standard greeting, or a personal one that's there. */
export function withGreetingPlaying(g: Greetings, choice: 'standard' | { slot: string }): Greetings {
  if (choice === 'standard') return { plays: 'standard', personal: { ...g.personal } };
  if (!SLOT.test(choice.slot) || !g.personal[choice.slot]) throw new GreetingChoiceError(`There’s no personal greeting ${choice.slot} to play.`);
  return { plays: `personal:${choice.slot}`, personal: { ...g.personal } };
}

/** For summaries and history: "the standard greeting", "typed: “Hi…”", "recorded greeting 2". */
export function describeGreetings(value: string): string {
  const g = parseGreetings(value);
  if (!g) return value ? 'a greeting set on the phone system' : '(none)';
  const now = playingNow(g);
  switch (now.kind) {
    case 'standard':
      return 'the standard greeting';
    case 'typed':
      return `typed: “${now.text.length > 40 ? `${now.text.slice(0, 40)}…` : now.text}”`;
    case 'recorded':
      return `recorded greeting ${now.slot}`;
    default:
      return 'a greeting set on the phone system';
  }
}

/**
 * Why going from one value to another would touch a recording (change, empty or add one), or null
 * when every recording stays as it is. Stitch never changes what someone recorded.
 */
export function recordingTouched(before: string, after: string): string | null {
  const a = parseGreetings(before);
  const b = parseGreetings(after);
  if (!a || !b) return null;
  for (const n of new Set([...Object.keys(a.personal), ...Object.keys(b.personal)])) {
    const was = a.personal[n];
    const will = b.personal[n];
    if (isRecorded(was) && (!isRecorded(will) || will.file !== was.file)) return `Personal greeting ${n} is a recording, and Stitch never changes or removes one`;
    if (isRecorded(will) && (!isRecorded(was) || was.file !== will.file)) return 'Recorded greetings are made on the phone, not in Stitch';
  }
  return null;
}

/**
 * Whether `now` lies between `before` and `after`: which greeting plays is one or the other, and
 * so is each personal greeting. Greetings are saved in two steps (the words, then which plays), so
 * a change that stopped part-way leaves something in between, which is still Stitch's own doing.
 */
export function greetingBetween(before: string, after: string, now: string): boolean {
  const a = parseGreetings(before);
  const b = parseGreetings(after);
  const c = parseGreetings(now);
  if (!a || !b || !c) return false;
  if (c.plays !== a.plays && c.plays !== b.plays) return false;
  const same = (x: PersonalGreeting | undefined, y: PersonalGreeting | undefined) => JSON.stringify(x ?? null) === JSON.stringify(y ?? null);
  const norm = (g: Greetings) => parseGreetings(formatGreetings(g))!;
  const [na, nb, nc] = [norm(a), norm(b), norm(c)];
  for (const n of new Set([...Object.keys(na.personal), ...Object.keys(nb.personal), ...Object.keys(nc.personal)])) {
    if (!same(nc.personal[n], na.personal[n]) && !same(nc.personal[n], nb.personal[n])) return false;
  }
  return true;
}

function slotsInOrder(g: Greetings): string[] {
  return Object.keys(g.personal).sort((a, b) => Number(a) - Number(b));
}
